import { MouseEvent, useContext, useMemo } from 'react';
import {
  CaretDown,
  SquareSplitHorizontal,
  CaretRight,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import { Counter } from 'src/v2/commonComponents/Counter';
import SessionContext from 'src/contexts/SessionContext';
import { DEFAULT_CHAT_NAME, SVG_SIZE_S } from 'src/constants';
import { Conversation } from 'src/types';
import { ConversationMenu } from '../ConversationMenu';
import { Link } from 'react-router-dom';
import {
  useComparingConversationsParams,
  useConversationLink,
} from 'src/hooks';
import { convertTaskHashToTitle } from 'src/utils';
import styles from './ConversationTitle.module.scss';

interface ConversationTitleProps {
  isSelected: boolean;
  isExpanded: boolean;
  hasTasks: boolean;
  activeThreadAnchor: string | null;
  conversation: Conversation;
  toggleProps: {
    onClick?: React.MouseEventHandler<Element>;
    disabled?: boolean;
    [k: string]: unknown;
  };
  setIsExpanded: (isExpanded: boolean) => void;
}

export const ConversationTitle = ({
  isSelected,
  setIsExpanded,
  conversation,
  isExpanded,
  hasTasks,
  activeThreadAnchor,
  toggleProps,
}: ConversationTitleProps) => {
  const { firstComparedConvId } = useComparingConversationsParams();

  const {
    linked_conversation_id,
    conversation_hash,
    number_tasks_require_attention,
    conversation_id,
    messages,
  } = conversation;

  const { isVisibleTaskViewModal } = useContext(SessionContext);
  const { linkTo } = useConversationLink({
    conversationId: conversation_id,
    linkedConversationId: linked_conversation_id,
  });

  const threadId = conversation_id || DEFAULT_CHAT_NAME;

  const titleWithoutHash = useMemo(
    () => conversation_hash?.replace('#', '') || '',
    [conversation_hash],
  );

  const handleTitleClick = (e: MouseEvent) => {
    toggleProps.onClick?.(e);
  };

  const toggleExpanded = (e: MouseEvent) => {
    setIsExpanded(!isExpanded);
  };

  const isFirstComparedConv = firstComparedConvId === conversation_id;

  const shouldDisplayConversationMenu = linked_conversation_id
    ? isSelected && !isVisibleTaskViewModal && isFirstComparedConv
    : isSelected && !isVisibleTaskViewModal;

  return (
    <Link
      className={classNames(styles.root, {
        [styles.default]: threadId === DEFAULT_CHAT_NAME,
        [styles.hasMessages]: !!messages?.length,
        [styles.selected]: isSelected,
      })}
      to={linkTo}
      data-conversation-id={conversation_id}
      data-conversation-hash={conversation_hash}
    >
      <div
        className={classNames(styles.container, {
          [styles.withIndicator]: isSelected && !activeThreadAnchor,
        })}
      >
        {hasTasks && (
          <button className={styles.collapsedIcon} onClick={toggleExpanded}>
            {isExpanded ? (
              <CaretDown size={SVG_SIZE_S} />
            ) : (
              <CaretRight size={SVG_SIZE_S} />
            )}
          </button>
        )}

        <h3
          className={classNames(styles.conversationTitle, {
            [styles.hasNotification]: !!number_tasks_require_attention,
          })}
          data-e2e="conversation-title"
          onClick={handleTitleClick}
        >
          <span className={styles.title}>
            {convertTaskHashToTitle(titleWithoutHash)}
          </span>

          {linked_conversation_id && (
            <SquareSplitHorizontal size={SVG_SIZE_S} />
          )}
        </h3>

        {!!number_tasks_require_attention && (
          <Counter
            number={number_tasks_require_attention}
            className={styles.counter}
          />
        )}

        <div className={styles.threeDotsMenu}>
          {shouldDisplayConversationMenu && (
            <ConversationMenu conversation={conversation} />
          )}
        </div>
      </div>
    </Link>
  );
};
